import {
  LayerEvacZone,
  EvacZoneStatus,
  RegionEvacZoneStyle,
} from 'shared/types';
import { Poi } from 'features/mapping/mapping.types';
import { getWatchdutyStaticTileUrl } from 'features/mapping/components/layers/layers.utils';
import { statusSortingComparator } from 'features/evacZones/utils/statusSortingComparator';
import {
  ADVISORIES_DEFAULT_COLOR,
  ADVISORIES_DEFAULT_PATTERN,
  ADVISORIES_GREEN_PATTERN,
  ADVISORIES_REVERSE_PATTERN,
  ADVISORIES_RSG_COLOR,
  EVAC_ZONE_STATUS_ENABLED,
  EvacZoneStatuses,
  EvacZoneStyles,
  ORDERS_COLOR,
  ORDERS_PATTERN,
  ORDERS_REVERSE_PATTERN,
  SHELTER_IN_PLACE_COLOR,
  SHELTER_IN_PLACE_PATTERN,
  WARNINGS_COLOR,
  WARNINGS_PATTERN,
} from '../../../../../constants';
import { StyleStatusGrouping } from './types';
import {
  SOURCE_ID,
  EVAC_ZONES_EDIT_LAYER_ID_PREFIX,
  PENDING_EVAC_ZONES_EDIT_LAYER_ID_PREFIX,
} from './constants';

type EvacZoneStatusFeature = { properties?: { status?: EvacZoneStatus | '' } };

/*
 *  Please note that the order from advisory -> warning -> orders ensures
 *  that the highest priority color on the stroke perimeters is on top where they touch
 */
export const createColorGroupings = (
  groupings: StyleStatusGrouping,
): Record<string, string[]> => ({
  [ADVISORIES_DEFAULT_COLOR]: [
    ...groupings[EvacZoneStyles.default][EvacZoneStatuses.advisories],
  ],
  [ADVISORIES_RSG_COLOR]: [
    ...groupings[EvacZoneStyles.readySetGo][EvacZoneStatuses.advisories],
    ...groupings[EvacZoneStyles.withLevels][EvacZoneStatuses.advisories],
    ...groupings[EvacZoneStyles.reverseColors][EvacZoneStatuses.orders],
  ],
  [WARNINGS_COLOR]: [
    ...groupings[EvacZoneStyles.default][EvacZoneStatuses.warnings],
    ...groupings[EvacZoneStyles.readySetGo][EvacZoneStatuses.warnings],
    ...groupings[EvacZoneStyles.withLevels][EvacZoneStatuses.warnings],
    ...groupings[EvacZoneStyles.reverseColors][EvacZoneStatuses.warnings],
    ...groupings[EvacZoneStyles.readySetShelter][EvacZoneStatuses.warnings],
  ],
  [ORDERS_COLOR]: [
    ...groupings[EvacZoneStyles.default][EvacZoneStatuses.orders],
    ...groupings[EvacZoneStyles.readySetGo][EvacZoneStatuses.orders],
    ...groupings[EvacZoneStyles.withLevels][EvacZoneStatuses.orders],
    ...groupings[EvacZoneStyles.reverseColors][EvacZoneStatuses.advisories],
    ...groupings[EvacZoneStyles.readySetShelter][EvacZoneStatuses.orders],
  ],
  [SHELTER_IN_PLACE_COLOR]: [
    ...groupings[EvacZoneStyles.default][EvacZoneStatuses.shelterInPlace],
    ...groupings[EvacZoneStyles.readySetGo][EvacZoneStatuses.shelterInPlace],
    ...groupings[EvacZoneStyles.withLevels][EvacZoneStatuses.shelterInPlace],
    ...groupings[EvacZoneStyles.reverseColors][EvacZoneStatuses.shelterInPlace],
    ...groupings[EvacZoneStyles.readySetShelter][
      EvacZoneStatuses.shelterInPlace
    ],
  ],
});

export const createPatternGroupings = (
  groupings: StyleStatusGrouping,
): Record<string, string[]> => ({
  [ADVISORIES_DEFAULT_PATTERN]: [
    ...groupings[EvacZoneStyles.default][EvacZoneStatuses.advisories],
  ],
  [ADVISORIES_GREEN_PATTERN]: [
    ...groupings[EvacZoneStyles.readySetGo][EvacZoneStatuses.advisories],
    ...groupings[EvacZoneStyles.withLevels][EvacZoneStatuses.advisories],
  ],
  [ADVISORIES_REVERSE_PATTERN]: [
    ...groupings[EvacZoneStyles.reverseColors][EvacZoneStatuses.advisories],
  ],
  [WARNINGS_PATTERN]: [
    ...groupings[EvacZoneStyles.default][EvacZoneStatuses.warnings],
    ...groupings[EvacZoneStyles.readySetGo][EvacZoneStatuses.warnings],
    ...groupings[EvacZoneStyles.withLevels][EvacZoneStatuses.warnings],
    ...groupings[EvacZoneStyles.reverseColors][EvacZoneStatuses.warnings],
    ...groupings[EvacZoneStyles.readySetShelter][EvacZoneStatuses.warnings],
  ],
  [ORDERS_PATTERN]: [
    ...groupings[EvacZoneStyles.default][EvacZoneStatuses.orders],
    ...groupings[EvacZoneStyles.readySetGo][EvacZoneStatuses.orders],
    ...groupings[EvacZoneStyles.withLevels][EvacZoneStatuses.orders],
    ...groupings[EvacZoneStyles.readySetShelter][EvacZoneStatuses.orders],
  ],
  [ORDERS_REVERSE_PATTERN]: [
    ...groupings[EvacZoneStyles.reverseColors][EvacZoneStatuses.orders],
  ],
  [SHELTER_IN_PLACE_PATTERN]: [
    ...groupings[EvacZoneStyles.default][EvacZoneStatuses.shelterInPlace],
    ...groupings[EvacZoneStyles.readySetGo][EvacZoneStatuses.shelterInPlace],
    ...groupings[EvacZoneStyles.withLevels][EvacZoneStatuses.shelterInPlace],
    ...groupings[EvacZoneStyles.reverseColors][EvacZoneStatuses.shelterInPlace],
    ...groupings[EvacZoneStyles.readySetShelter][
      EvacZoneStatuses.shelterInPlace
    ],
  ],
});

export const getFillLayerId = (
  color: string,
  layerIdPrefix: string,
): string => {
  return `${layerIdPrefix}-fill-${color}`;
};

export const getActiveZonesLayerIds = (): string[] => {
  return [
    EVAC_ZONES_EDIT_LAYER_ID_PREFIX,
    PENDING_EVAC_ZONES_EDIT_LAYER_ID_PREFIX,
  ]
    .map((prefix) => {
      return [
        ADVISORIES_DEFAULT_COLOR,
        ADVISORIES_RSG_COLOR,
        WARNINGS_COLOR,
        ORDERS_COLOR,
        SHELTER_IN_PLACE_COLOR,
      ].map((color) => getFillLayerId(color, prefix));
    })
    .flat();
};

export const switchZoneStatus = (
  evacZoneStyle: RegionEvacZoneStyle,
  currentStatus?: EvacZoneStatus,
): EvacZoneStatus | undefined => {
  const styleStatuses = EVAC_ZONE_STATUS_ENABLED[evacZoneStyle] || {};
  const statuses: EvacZoneStatus[] = [
    EvacZoneStatuses.shelterInPlace,
    EvacZoneStatuses.advisories,
    EvacZoneStatuses.warnings,
    EvacZoneStatuses.orders,
  ];
  const currentIndex = currentStatus ? statuses.indexOf(currentStatus) : -1;
  const nextIndex = currentIndex + 1 > statuses.length ? 0 : currentIndex + 1;
  const nextStyle = statuses[nextIndex];
  // skip any statuses that are not enabled for the current style
  if (nextStyle && !styleStatuses[nextStyle]) {
    return switchZoneStatus(evacZoneStyle, nextStyle);
  }
  return nextStyle;
};

export const createGroupings = (
  evacZones: LayerEvacZone[],
): StyleStatusGrouping => {
  const groups: StyleStatusGrouping = {
    [EvacZoneStyles.default]: {
      [EvacZoneStatuses.warnings]: [],
      [EvacZoneStatuses.advisories]: [],
      [EvacZoneStatuses.orders]: [],
      [EvacZoneStatuses.shelterInPlace]: [],
    },
    [EvacZoneStyles.readySetGo]: {
      [EvacZoneStatuses.warnings]: [],
      [EvacZoneStatuses.advisories]: [],
      [EvacZoneStatuses.orders]: [],
      [EvacZoneStatuses.shelterInPlace]: [],
    },
    [EvacZoneStyles.reverseColors]: {
      [EvacZoneStatuses.warnings]: [],
      [EvacZoneStatuses.advisories]: [],
      [EvacZoneStatuses.orders]: [],
      [EvacZoneStatuses.shelterInPlace]: [],
    },
    [EvacZoneStyles.withLevels]: {
      [EvacZoneStatuses.warnings]: [],
      [EvacZoneStatuses.advisories]: [],
      [EvacZoneStatuses.orders]: [],
      [EvacZoneStatuses.shelterInPlace]: [],
    },
    [EvacZoneStyles.readySetShelter]: {
      [EvacZoneStatuses.warnings]: [],
      [EvacZoneStatuses.advisories]: [],
      [EvacZoneStatuses.orders]: [],
      [EvacZoneStatuses.shelterInPlace]: [],
    },
  };

  evacZones.forEach((zone) => {
    if (!zone.status) {
      return;
    }
    const style = zone.style || EvacZoneStyles.default;
    groups[style][zone.status].push(zone.uidV2);
  });
  return groups;
};

export const getTilesUrl = (cacheBuster?: number | undefined): string => {
  return `${getWatchdutyStaticTileUrl(SOURCE_ID)}${
    cacheBuster ? `?ts=${cacheBuster}` : ''
  }`;
};

export const getSelectedEvacZoneUId = (
  selectedPoi?: Poi,
  isActiveEvacZone: boolean = false,
): string => {
  const expectedType = isActiveEvacZone ? 'activeEvacZone' : 'evacZone';
  return selectedPoi?.id && selectedPoi.type === expectedType
    ? selectedPoi.id.toString()
    : '';
};

// `uid_v2` of the selected evac zone only when it has no status, else ''.
// Zones with a status are in the tiles at every zoom and don't need the overlay.
export const getSelectedNoStatusEvacZoneUId = (selectedPoi?: Poi): string => {
  if (
    !selectedPoi?.id ||
    (selectedPoi.type !== 'evacZone' && selectedPoi.type !== 'activeEvacZone')
  ) {
    return '';
  }
  return selectedPoi.metadata?.status ? '' : selectedPoi.id.toString();
};

export const evacZoneFeatureComparator = (
  a: EvacZoneStatusFeature,
  b: EvacZoneStatusFeature,
): number =>
  statusSortingComparator(
    a.properties?.status || undefined,
    b.properties?.status || undefined,
  );
